import { Add, Delete, Edit } from '@mui/icons-material';
import {
    Box,
    Breadcrumbs,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import React, { useCallback, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import api from '../../api';
import DidNotLoad from '../../components/DidNotLoad';
import Loading from '../../components/Loading';
import MoneyInput from '../../components/MoneyInput';
import { useListFetch } from '../../hooks/useListFetch';
import { parseMoneyToCents } from '../../helpers/formatters';

const emptyForm = () => ({
    name: '',
    commissionPercent: '',
    fixedFeeReais: '',
    paymentFeePercent: '',
    minFeeReais: '',
    dispatchDays: '',
    notes: '',
});

function formatPercent(value) {
    if (value == null || value === '') {
        return '—';
    }
    const n = Number(value);
    if (Number.isNaN(n)) {
        return '—';
    }
    return `${n.toLocaleString('pt-BR', { maximumFractionDigits: 3 })}%`;
}

function formatMoneyCents(cents) {
    const n = Number(cents) || 0;
    if (n <= 0) {
        return '—';
    }
    return (n / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function SalesChannelsPage() {
    const navigate = useNavigate();
    const [channels, setChannels] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [didNotLoad, setDidNotLoad] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(emptyForm());
    const [nameError, setNameError] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    const fetchChannels = useCallback(() => {
        setIsLoading(true);
        return api.get('sales-channels')
            .then((response) => {
                setChannels(Array.isArray(response.data?.channels) ? response.data.channels : []);
                setDidNotLoad(false);
            })
            .catch(() => {
                setDidNotLoad(true);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, []);

    useListFetch(fetchChannels, [fetchChannels]);

    const updateForm = (patch) => {
        setForm((current) => ({ ...current, ...patch }));
    };

    const openCreate = () => {
        setEditing(null);
        setForm(emptyForm());
        setNameError('');
        setDialogOpen(true);
    };

    const openEdit = (channel) => {
        setEditing(channel);
        setForm({
            name: channel.name || '',
            commissionPercent: channel.commission_percent != null ? String(channel.commission_percent) : '',
            fixedFeeReais: channel.fixed_fee ? (Number(channel.fixed_fee) / 100).toFixed(2) : '',
            paymentFeePercent: channel.payment_fee_percent != null ? String(channel.payment_fee_percent) : '',
            minFeeReais: channel.min_fee ? (Number(channel.min_fee) / 100).toFixed(2) : '',
            dispatchDays: channel.dispatch_days != null && channel.dispatch_days !== ''
                ? String(channel.dispatch_days)
                : '',
            notes: channel.notes || '',
        });
        setNameError('');
        setDialogOpen(true);
    };

    const closeDialog = () => {
        if (isSaving) {
            return;
        }
        setDialogOpen(false);
    };

    const handleSave = (event) => {
        event.preventDefault();
        const trimmed = String(form.name || '').trim();
        if (!trimmed) {
            setNameError('Informe o nome do canal.');
            return;
        }
        if (trimmed.length > 80) {
            setNameError('O nome deve ter no máximo 80 caracteres.');
            return;
        }

        const payload = {
            name: trimmed,
            commission_percent: form.commissionPercent === '' ? 0 : Number(form.commissionPercent),
            fixed_fee: parseMoneyToCents(form.fixedFeeReais),
            payment_fee_percent: form.paymentFeePercent === '' ? 0 : Number(form.paymentFeePercent),
            min_fee: parseMoneyToCents(form.minFeeReais),
            notes: String(form.notes || '').trim(),
        };
        if (form.dispatchDays !== '') {
            payload.dispatch_days = Number(form.dispatchDays);
        } else {
            payload.dispatch_days = null;
        }

        setIsSaving(true);
        setNameError('');
        const request = editing
            ? api.put(`sales-channels/${editing.uuid || editing.id}`, payload)
            : api.post('sales-channels', payload);

        request
            .then(() => {
                setDialogOpen(false);
                return fetchChannels();
            })
            .catch((error) => {
                const message = error?.response?.data?.error
                    || error?.response?.data?.message
                    || 'Não foi possível salvar o canal de vendas.';
                setNameError(message);
            })
            .finally(() => {
                setIsSaving(false);
            });
    };

    const handleDelete = (channel) => {
        Swal.fire({
            title: 'Remover canal?',
            text: `O canal "${channel.name}" será removido. Vendas já registradas continuam intactas (o vínculo fica vazio).`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Sim, remover',
            cancelButtonText: 'Cancelar',
        }).then((result) => {
            if (!result.isConfirmed) {
                return;
            }
            api.delete(`sales-channels/${channel.uuid || channel.id}`)
                .then(() => fetchChannels())
                .catch((error) => {
                    const message = error?.response?.data?.error
                        || error?.response?.data?.message
                        || error?.message
                        || 'Não foi possível remover o canal.';
                    Swal.fire('Erro', message, 'error');
                });
        });
    };

    if (isLoading && channels.length === 0) {
        return <Loading />;
    }

    if (didNotLoad && channels.length === 0) {
        return <DidNotLoad />;
    }

    return (
        <div>
            <Breadcrumbs aria-label="breadcrumb">
                <Link color="inherit" onClick={() => navigate('/admin')} to="/admin">
                    Dashboard
                </Link>
                <Link color="inherit" onClick={() => navigate('/admin/my-account')} to="/admin/my-account">
                    Minha Conta
                </Link>
                <Typography color="text.primary">Canais de vendas</Typography>
            </Breadcrumbs>
            <br />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
                <Box>
                    <Typography variant="h4" gutterBottom>
                        Canais de vendas
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Configure comissão, taxas e prazo de envio de cada canal (Shopee, Mercado Livre, etc.).
                    </Typography>
                </Box>
                <Button variant="contained" startIcon={<Add />} onClick={openCreate}>
                    Novo canal
                </Button>
            </Box>

            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>Nome</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Comissão</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Taxa fixa</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Cobrança</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Envio</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', width: 120 }} align="right">Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {channels.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6}>
                                    Nenhum canal cadastrado ainda.
                                </TableCell>
                            </TableRow>
                        ) : (
                            channels.map((channel) => (
                                <TableRow key={channel.uuid || channel.id}>
                                    <TableCell>{channel.name}</TableCell>
                                    <TableCell>{formatPercent(channel.commission_percent)}</TableCell>
                                    <TableCell>{formatMoneyCents(channel.fixed_fee)}</TableCell>
                                    <TableCell>{formatPercent(channel.payment_fee_percent)}</TableCell>
                                    <TableCell>
                                        {channel.dispatch_days != null
                                            ? `${channel.dispatch_days} dia(s)`
                                            : '—'}
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton color="primary" onClick={() => openEdit(channel)} aria-label="editar">
                                            <Edit />
                                        </IconButton>
                                        <IconButton color="error" onClick={() => handleDelete(channel)} aria-label="remover">
                                            <Delete />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="md">
                <form onSubmit={handleSave}>
                    <DialogTitle>{editing ? 'Editar canal' : 'Novo canal'}</DialogTitle>
                    <DialogContent>
                        <Grid container spacing={2} sx={{ mt: 0.5 }}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    autoFocus
                                    label="Nome do canal"
                                    fullWidth
                                    value={form.name}
                                    onChange={(e) => updateForm({ name: e.target.value })}
                                    error={Boolean(nameError)}
                                    helperText={nameError || 'Ex.: Presencial, Online, Shopee, Mercado Livre'}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    label="Prazo para envio (dias)"
                                    fullWidth
                                    type="number"
                                    inputProps={{ min: 0, step: 1 }}
                                    value={form.dispatchDays}
                                    onChange={(e) => updateForm({ dispatchDays: e.target.value })}
                                    helperText="Prazo típico do marketplace para despachar o pedido"
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    label="Comissão (%)"
                                    fullWidth
                                    type="number"
                                    inputProps={{ min: 0, max: 100, step: 0.001 }}
                                    value={form.commissionPercent}
                                    onChange={(e) => updateForm({ commissionPercent: e.target.value })}
                                    helperText="Ex.: Shopee ~14–22%, ML conforme categoria"
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <MoneyInput
                                    label="Taxa fixa por item"
                                    value={form.fixedFeeReais}
                                    onChange={(value) => updateForm({ fixedFeeReais: value })}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    label="Taxa de cobrança (%)"
                                    fullWidth
                                    type="number"
                                    inputProps={{ min: 0, max: 100, step: 0.001 }}
                                    value={form.paymentFeePercent}
                                    onChange={(e) => updateForm({ paymentFeePercent: e.target.value })}
                                    helperText="Ex.: taxa de pagamento da Shopee"
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <MoneyInput
                                    label="Taxa mínima"
                                    value={form.minFeeReais}
                                    onChange={(value) => updateForm({ minFeeReais: value })}
                                />
                            </Grid>
                            <Grid item xs={12} md={8}>
                                <TextField
                                    label="Observações"
                                    fullWidth
                                    multiline
                                    minRows={2}
                                    value={form.notes}
                                    onChange={(e) => updateForm({ notes: e.target.value })}
                                    helperText="Categoria, tipo de anúncio (clássico/premium), campanhas, etc."
                                    inputProps={{ maxLength: 500 }}
                                />
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={closeDialog} disabled={isSaving}>Cancelar</Button>
                        <Button type="submit" variant="contained" disabled={isSaving} startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : null}>
                            {isSaving ? 'Salvando...' : 'Salvar'}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>
        </div>
    );
}

export default SalesChannelsPage;
